import { useState, useRef } from 'react';

type Product = {
  name: string;
  category: string;
  description: string;
  pricing: string;
  rating: number;
  reviews: number;
  commission: string;
  image: string;
  tags: string[];
  features: string[];
};

interface ImportModalProps {
  onClose: () => void;
  onImport: (products: Product[]) => void;
}

export default function ImportModal({ onClose, onImport }: ImportModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<Product[]>([]);
  const [error, setError] = useState('');
  const [step, setStep] = useState<'upload' | 'preview'>('upload');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    if (!selectedFile.name.endsWith('.csv')) {
      setError('Please upload a CSV file');
      return;
    }

    setFile(selectedFile);
    setError('');
    parseCSV(selectedFile);
  };

  const parseCSV = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target?.result as string;
        const lines = text.split('\n').filter(line => line.trim());
        
        if (lines.length < 2) {
          setError('CSV file is empty or invalid');
          return;
        }

        const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
        const products: Product[] = [];

        for (let i = 1; i < lines.length; i++) {
          const values = parseCSVLine(lines[i]);
          
          const product: Product = {
            name: getValueByHeader(headers, values, ['name', 'product name', 'product']) || '',
            category: getValueByHeader(headers, values, ['category', 'type']) || '',
            description: getValueByHeader(headers, values, ['description', 'desc']) || '',
            pricing: getValueByHeader(headers, values, ['pricing', 'price']) || '',
            rating: parseFloat(getValueByHeader(headers, values, ['rating', 'stars']) || '4.5'),
            reviews: parseInt(getValueByHeader(headers, values, ['reviews', 'review count']) || '0'),
            commission: getValueByHeader(headers, values, ['commission', 'comm']) || '',
            image: getValueByHeader(headers, values, ['image', 'image url', 'img']) || '',
            tags: parseArray(getValueByHeader(headers, values, ['tags', 'tag'])),
            features: parseArray(getValueByHeader(headers, values, ['features', 'feature'])),
          };

          if (product.name) {
            products.push(product);
          }
        }

        if (products.length === 0) {
          setError('No valid products found in CSV');
          return;
        }

        setPreviewData(products);
        setStep('preview');
      } catch (err) {
        setError('Failed to parse CSV file. Please check the format.');
        console.error(err);
      }
    };
    reader.readAsText(file);
  };

  const parseCSVLine = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    return result;
  };

  const getValueByHeader = (headers: string[], values: string[], possibleNames: string[]): string => {
    for (const name of possibleNames) {
      const index = headers.indexOf(name);
      if (index !== -1 && values[index]) {
        return values[index].replace(/^"|"$/g, '');
      }
    }
    return '';
  };

  const parseArray = (value: string): string[] => {
    if (!value) return [];
    return value.split('|').map(v => v.trim()).filter(v => v);
  };

  const handleImport = () => {
    onImport(previewData);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.name.endsWith('.csv')) {
      setFile(droppedFile);
      setError('');
      parseCSV(droppedFile);
    } else {
      setError('Please drop a CSV file');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Import Products from CSV</h2>
            <p className="text-sm text-gray-500 mt-1">
              {step === 'upload' ? 'Upload your Airtable CSV export' : `Preview ${previewData.length} products`}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {step === 'upload' ? (
            <div>
              {/* Upload Area */}
              <div
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center hover:border-teal-500 transition-colors cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
              >
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-gray-100 rounded-full">
                  <i className="ri-upload-cloud-2-line text-3xl text-gray-400"></i>
                </div>
                <p className="text-gray-900 font-semibold mb-2">
                  {file ? file.name : 'Drop your CSV file here or click to browse'}
                </p>
                <p className="text-sm text-gray-500">Supports CSV files exported from Airtable</p>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                className="hidden"
              />

              {error && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                  <i className="ri-error-warning-line text-red-600 text-lg mt-0.5"></i>
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}

              {/* Format Guide */}
              <div className="mt-8 p-6 bg-gray-50 rounded-lg">
                <h3 className="text-sm font-bold text-gray-900 mb-3">Expected CSV Format</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Your CSV should include the following columns (column names are flexible):
                </p>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-teal-500 mt-0.5"></i>
                    <div>
                      <span className="font-semibold text-gray-900">Name</span>
                      <span className="text-gray-500"> (required)</span>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-teal-500 mt-0.5"></i>
                    <div>
                      <span className="font-semibold text-gray-900">Category</span>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-teal-500 mt-0.5"></i>
                    <div>
                      <span className="font-semibold text-gray-900">Description</span>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-teal-500 mt-0.5"></i>
                    <div>
                      <span className="font-semibold text-gray-900">Pricing</span>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-teal-500 mt-0.5"></i>
                    <div>
                      <span className="font-semibold text-gray-900">Rating</span>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-teal-500 mt-0.5"></i>
                    <div>
                      <span className="font-semibold text-gray-900">Reviews</span>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-teal-500 mt-0.5"></i>
                    <div>
                      <span className="font-semibold text-gray-900">Commission</span>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-teal-500 mt-0.5"></i>
                    <div>
                      <span className="font-semibold text-gray-900">Image URL</span>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-teal-500 mt-0.5"></i>
                    <div>
                      <span className="font-semibold text-gray-900">Tags</span>
                      <span className="text-gray-500"> (use | separator)</span>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-teal-500 mt-0.5"></i>
                    <div>
                      <span className="font-semibold text-gray-900">Features</span>
                      <span className="text-gray-500"> (use | separator)</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div>
              {/* Preview Table */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto max-h-96">
                  <table className="w-full">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Product</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Category</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Pricing</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Rating</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Commission</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {previewData.map((product, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              {product.image && (
                                <div className="w-10 h-10 bg-gray-100 flex-shrink-0 overflow-hidden">
                                  <img src={product.image} alt="" className="w-full h-full object-cover object-top" />
                                </div>
                              )}
                              <div>
                                <p className="text-sm font-semibold text-gray-900">{product.name}</p>
                                <p className="text-xs text-gray-500 line-clamp-1">{product.description}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-gray-600">{product.category}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm font-semibold text-gray-900">{product.pricing}</span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              <i className="ri-star-fill text-yellow-500 text-sm"></i>
                              <span className="text-sm text-gray-900">{product.rating}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm font-semibold text-teal-600">{product.commission}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="mt-4 p-4 bg-teal-50 border border-teal-200 rounded-lg flex items-start gap-3">
                <i className="ri-information-line text-teal-600 text-lg mt-0.5"></i>
                <p className="text-sm text-teal-800">
                  {previewData.length} products ready to import. Click "Import Products" to add them to your catalog.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
          {step === 'preview' && (
            <button
              onClick={() => {
                setStep('upload');
                setPreviewData([]);
                setFile(null);
              }}
              className="px-5 py-2.5 text-gray-700 font-semibold hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              Back
            </button>
          )}
          <button
            onClick={onClose}
            className="px-5 py-2.5 text-gray-700 font-semibold hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            Cancel
          </button>
          {step === 'preview' && (
            <button
              onClick={handleImport}
              className="px-5 py-2.5 bg-teal-500 text-white font-semibold rounded-lg hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Import Products
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
